import { AiCapturedBadge } from "@prototypes/ai-capture/AiCapturedBadge";
import classNames from "classnames";
import { format, fromZonedTime, toZonedTime } from "date-fns-tz";
import { GeoPoint, Timestamp } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import type { ReservationTimestamp } from "../../common/types/ReservationStatus";
import { isEmpty } from "../../common/utils/string.util";
import {
    dateWithShortMonthWithTimeFormat,
    LocalTime,
    timeToDate,
    toDateLocalInputValue,
} from "../../common/utils/time.util";
import {
    dateWithLocalNoonTime,
    dateWithShortMonthFormat,
    defaultDateTimeFormat,
    defaultTimezone,
    getTimezoneByGeopoint,
    gmtDifference,
} from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import HandleClicksOutside from "../HandleClicksOutside";
import SearchLocationInput from "../SearchLocationInput/SearchLocationInput";
import Dropdown from "./Dropdown";
import "./EditableDateInput.css";
import Button from "./Buttons";
import { Flex } from "./Flex";
import Icon from "./Icon";
import Label from "./Label";

const cityPlaceholderText = "Add city to switch timezone...";

type LocalDateTimeProps = {
    label?: string;
    initialDateValue: string | null | undefined;
    initialTimeValue: string | null | undefined;
    minDateValue?: string | null | undefined;
    minTimeValue?: string | null | undefined;
    maxDateValue?: string | null | undefined;
    maxTimeValue?: string | null | undefined;
    onDateChange: (date: string | null) => void;
    onTimeChange: (time: string | null) => void;
    className?: string;
    highlightDate?: boolean;
    highlightTime?: boolean;
};

export type AutofillSelection = {
    value: string;
    label: string;
    timestamp: Timestamp | null;
    timezone: string | null;
};

type ZonedDateTimeProps = {
    dateLabel: string;
    errorHighlight?: boolean;
    value: ReservationTimestamp;
    autofillOptions?: AutofillSelection[];
    className?: string;
    onChange: (value: ReservationTimestamp) => void;
};

export const EditableLocalDateInput = function EditableLocalDateInput(
    props: LocalDateTimeProps,
) {
    const {
        className,
        label,
        initialDateValue,
        initialTimeValue,
        minDateValue,
        minTimeValue,
        maxDateValue,
        maxTimeValue,
        onDateChange,
        onTimeChange,
        highlightDate,
        highlightTime,
    } = props;

    const [date, setDate] = useState<Date | null>(
        initialDateValue ? dateWithLocalNoonTime(initialDateValue) : null,
    );
    const [dateValue, setDateValue] = useState<string>(initialDateValue || "");
    const [time, setTime] = useState<string>(initialTimeValue || "");

    useEffect(() => {
        setDate(initialDateValue ? dateWithLocalNoonTime(initialDateValue) : null);
        setDateValue(initialDateValue || "");
    }, [initialDateValue]);

    useEffect(() => {
        setTime(initialTimeValue || "");
    }, [initialTimeValue]);

    const timeToDateOptional = (HHmm: string | null | undefined) => {
        if (HHmm && !isEmpty(HHmm) && LocalTime.safeParse(HHmm).success) {
            return timeToDate(HHmm, new Date());
        }
        return undefined;
    };

    const timeAsDate = timeToDateOptional(time);
    const minDate =
        minDateValue && !isEmpty(minDateValue)
            ? dateWithLocalNoonTime(minDateValue)
            : undefined;
    const minTime = timeToDateOptional(minTimeValue);
    const maxDate =
        maxDateValue && !isEmpty(maxDateValue)
            ? dateWithLocalNoonTime(maxDateValue)
            : undefined;
    const maxTime = timeToDateOptional(maxTimeValue);

    const isSameAsMinDay = dateValue === minDateValue;
    const isSameAsMaxDay = dateValue === maxDateValue;

    const isInvalidDate = () => {
        const isAfterMaxDate = date && maxDate && date > maxDate;
        const isBeforeMinDate = date && minDate && date < minDate;
        return isAfterMaxDate || isBeforeMinDate;
    };

    const isInvalidTime = () => {
        const invalidTimeMin =
            timeAsDate && isSameAsMinDay && minTime && timeAsDate < minTime;
        const invalidTimeMax =
            timeAsDate && isSameAsMaxDay && maxTime && timeAsDate > maxTime;

        return invalidTimeMin || invalidTimeMax;
    };

    const isTimeOutOfBounds = (localDate: string | null | undefined): boolean => {
        const localDateIsAfterMaxTime =
            localDate === maxDateValue && timeAsDate && maxTime && timeAsDate > maxTime;
        if (localDateIsAfterMaxTime) {
            return true;
        }

        const localDateIsBeforeMinTime =
            localDate === minDateValue && timeAsDate && minTime && timeAsDate < minTime;
        if (localDateIsBeforeMinTime) {
            return true;
        }
        return false;
    };

    return (
        <div className={classNames("local-date-time", className)}>
            <label>{label}</label>
            <div className="local-date-time__value">
                <div
                    className={classNames(
                        "flex",
                        "mr-2",
                        highlightDate &&
                            "items-center rounded border border-blue-200 bg-blue-50 pr-1",
                        highlightDate && isInvalidDate() && "!border-destructive",
                    )}
                >
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        className={classNames("date-input__input", {
                            invalid: isInvalidDate(),
                            "!border-none !bg-transparent": highlightDate,
                        })}
                        showPopperArrow={false}
                        dateFormat={dateWithShortMonthFormat}
                        placeholderText="Date"
                        calendarStartDay={1}
                        todayButton="Today"
                        minDate={minDate}
                        maxDate={maxDate}
                        selected={date}
                        onChange={(newDate: Date | null) => {
                            const localDate: string | null = newDate
                                ? toDateLocalInputValue(newDate)
                                : null;

                            if (isTimeOutOfBounds(localDate)) {
                                onTimeChange(null);
                                setTime("");
                            }

                            onDateChange(localDate);
                            setDateValue(localDate || "");
                            if (localDate) {
                                setDate(dateWithLocalNoonTime(localDate)); // yyyy-MM-dd
                            }
                        }}
                    />
                    {highlightDate && <AiCapturedBadge inline />}
                </div>

                <div
                    className={classNames(
                        "flex",
                        highlightTime &&
                            "items-center rounded border border-blue-200 bg-blue-50 pr-1",
                        highlightTime && isInvalidTime() && "!border-destructive",
                    )}
                >
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <DatePicker
                        className={classNames("local-date-time__time", {
                            invalid: isInvalidTime(),
                            "!border-none !bg-transparent": highlightTime,
                        })}
                        showPopperArrow={false}
                        timeFormat="HH:mm"
                        dateFormat="HH:mm"
                        placeholderText="Time"
                        calendarStartDay={1}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        filterTime={(filterTime) => {
                            if (isEmpty(dateValue)) {
                                return true;
                            }
                            if (
                                isSameAsMinDay &&
                                isSameAsMaxDay &&
                                maxTime &&
                                minTime
                            ) {
                                return maxTime > filterTime && filterTime > minTime;
                            }
                            if (isSameAsMinDay && minTime) {
                                return filterTime > minTime;
                            }
                            if (isSameAsMaxDay && maxTime) {
                                return filterTime < maxTime;
                            }
                            return true;
                        }}
                        selected={timeToDateOptional(time)}
                        adjustDateOnChange
                        onChangeRaw={(event) => {
                            // Issue with react-datepicker 4.10.0 with inputting HH:mm formatted times
                            // https://github.com/Hacker0x01/react-datepicker/issues/3933
                            // This fix can be removed once it works again. For now drop the colons to prevent a crash
                            if (!time) {
                                event.currentTarget.value =
                                    event.currentTarget.value.replace(":", "");
                            }
                        }}
                        onBlur={(event) => {
                            const localTime = event.currentTarget.value;
                            if (time !== "" && localTime === "") {
                                onTimeChange(null);
                                setTime("");
                            }
                            if (localTime.length === 4) {
                                const firstTwo = localTime.substring(0, 2);
                                const secondTwo = localTime.substring(2, 4);
                                const localTimeFormatted = `${firstTwo}:${secondTwo}`;
                                if (LocalTime.safeParse(localTimeFormatted).success) {
                                    onTimeChange(localTimeFormatted);
                                    setTime(localTimeFormatted);
                                }
                            }
                        }}
                        onChange={(newTime) => {
                            const localTime = newTime ? format(newTime, "HH:mm") : null;
                            onTimeChange(localTime);
                            setTime(localTime || "");
                        }}
                    />
                    {highlightTime && <AiCapturedBadge inline />}
                </div>
            </div>
        </div>
    );
};

type AutofillDropdownProps = {
    visible: boolean;
    date: Date | null;
    timezone: string;
    errorHighlight?: boolean;
    autofillOptions?: AutofillSelection[];
    onChange: (value: AutofillSelection) => void;
};

const AutofillDropdown = function AutofillableZonedDateTimeInput(
    props: AutofillDropdownProps,
) {
    const { autofillOptions, date, onChange, visible, timezone, errorHighlight } =
        props;

    const [showPicker, setShowPicker] = useState<boolean>(false);

    const useCustomValue = "use_custom";
    const autofillOptionList = [
        ...(autofillOptions || []).map(({ value, label }) => ({
            value,
            label,
        })),
        {
            value: useCustomValue,
            label: "Custom date",
        },
    ];

    return visible ? (
        <Flex.Row>
            <Dropdown
                defaultLabel="Select"
                optionList={autofillOptionList}
                currentValue={null}
                onChange={(value) => {
                    if (value === useCustomValue) {
                        setShowPicker(true);
                        const newDate = new Date();
                        onChange({
                            value: format(newDate, defaultDateTimeFormat),
                            label: format(newDate, defaultDateTimeFormat),
                            timestamp: Timestamp.fromDate(newDate),
                            timezone,
                        });
                        return;
                    }

                    const foundAutofill = autofillOptions?.find(
                        (it) => it.value === value,
                    );

                    if (foundAutofill) {
                        onChange(foundAutofill);
                        setShowPicker(false);
                    }
                }}
            />
        </Flex.Row>
    ) : (
        <Flex.Row
            className={classNames({
                "date-input__value-container": true,
                "invalid-input": errorHighlight,
            })}
        >
            <Flex.Row className="date-input__value-container__date-picker">
                {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                <DatePicker
                    onInputClick={() => {
                        setShowPicker(true);
                    }}
                    onClickOutside={() => {
                        setShowPicker(false);
                    }}
                    open={showPicker}
                    showPopperArrow={false}
                    selected={date}
                    onChange={(newDate) => {
                        if (newDate) {
                            onChange({
                                value: format(newDate, defaultDateTimeFormat),
                                label: format(newDate, defaultDateTimeFormat),
                                timestamp: Timestamp.fromDate(newDate),
                                timezone,
                            });
                        } else {
                            onChange({
                                value: "",
                                label: "",
                                timestamp: null,
                                timezone,
                            });
                        }
                    }}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat={dateWithShortMonthWithTimeFormat}
                    placeholderText={dateWithShortMonthWithTimeFormat.toUpperCase()}
                    calendarStartDay={1}
                />
            </Flex.Row>
        </Flex.Row>
    );
};

type ZonedDateComponents = {
    timestamp: Date | null;
    timezone: string;
};

function reservationTimestampToZonedDateComponents(
    initialValue: ReservationTimestamp,
): ZonedDateComponents {
    const timezone = initialValue?.timezone || defaultTimezone;
    const timestamp = initialValue.timestamp
        ? toZonedTime(fromTimestampToDate(initialValue.timestamp), timezone)
        : null;
    return { timestamp, timezone };
}

export const EditableZonedDateTimeInput = function EditableZonedDateTimeInput(
    props: ZonedDateTimeProps,
) {
    const { autofillOptions, onChange, dateLabel, value, errorHighlight, className } =
        props;

    const [date, setDate] = useState<ZonedDateComponents>(
        reservationTimestampToZonedDateComponents(value),
    );
    const [showCitySelect, setShowCitySelect] = useState<boolean>();
    const searchCityInputRef = useRef<{
        input: () => HTMLInputElement | null;
    }>(null);
    const [showFillOptions, setShowFillOptions] = useState<boolean>(
        date.timestamp === null && autofillOptions?.length !== 0,
    );
    const [isPublished, setIsPublished] = useState<boolean>(false);

    useEffect(() => {
        setShowFillOptions(date.timestamp === null && autofillOptions?.length !== 0);
    }, [date.timestamp, autofillOptions]);

    useEffect(() => {
        setDate(reservationTimestampToZonedDateComponents(value));
    }, [value]);

    const publishDate = (newDate: Date | null, newTimezone: string) => {
        onChange({
            timestamp: newDate
                ? Timestamp.fromDate(fromZonedTime(newDate, newTimezone))
                : null,
            timezone: newTimezone,
        });
    };

    const publishIfChangedAfterLastPublish = (
        newDate: Date | null,
        newTimezone: string,
    ) => {
        if (!isPublished) {
            publishDate(newDate, newTimezone);
            setIsPublished(true);
        }
    };

    const setDraftDate = (newDate: Date | null, newTimezone: string) => {
        setDate({
            timestamp: newDate,
            timezone: newTimezone,
        });
        publishDate(newDate, newTimezone);
        setIsPublished(true);
    };

    const changeDateTime = (newDate: Date | null, newTimezone: string) => {
        publishDate(newDate, newTimezone);
        setDate({
            timestamp: newDate,
            timezone: newTimezone,
        });
    };

    const setCity = async (p: google.maps.places.PlaceResult | undefined) => {
        if (p?.geometry?.location) {
            const { location } = p.geometry;
            const timezoneResponse = await getTimezoneByGeopoint(
                new GeoPoint(location.lat(), location.lng()),
                new Date().getMilliseconds(),
            );

            if (timezoneResponse?.timeZoneId) {
                const newTimezone = timezoneResponse.timeZoneId;

                changeDateTime(date.timestamp, newTimezone);
                setShowCitySelect(false);
            }
        }
    };

    return (
        <HandleClicksOutside
            className={classNames("date-input zoned-date-time-input", className)}
            onClickOutside={() => {
                publishIfChangedAfterLastPublish(date.timestamp, date.timezone);
            }}
        >
            <Flex.Column gap="small">
                <div className="date-input__label">
                    <Label.XSmall.N50>{dateLabel}</Label.XSmall.N50>
                </div>
                <AutofillDropdown
                    visible={showFillOptions}
                    date={date.timestamp}
                    timezone={date.timezone}
                    autofillOptions={autofillOptions}
                    errorHighlight={errorHighlight}
                    onChange={({ timestamp, timezone }) => {
                        changeDateTime(
                            timestamp ? timestamp.toDate() : null,
                            timezone || date.timezone,
                        );
                        setDraftDate(timestamp?.toDate() || null, date.timezone);

                        setShowFillOptions(!timestamp);
                    }}
                />
                {date.timestamp && date.timezone && (
                    <Flex.Row
                        className="date-input__timezone-display"
                        gap="medium"
                        wrap="wrap"
                    >
                        <Label.Small.N10>{date.timezone}</Label.Small.N10>
                        <Label.Small.N50>
                            ({gmtDifference(date.timestamp, date.timezone)})
                        </Label.Small.N50>
                        <Button.Ghost
                            onClick={() => {
                                setShowCitySelect(true);
                            }}
                        >
                            <Label.Small.SemiBold.P50>
                                Change timezone
                            </Label.Small.SemiBold.P50>
                        </Button.Ghost>
                    </Flex.Row>
                )}
                {showCitySelect && (
                    <div className="date-input__city-container">
                        <SearchLocationInput
                            className="date-input__city"
                            placeholder={cityPlaceholderText}
                            ref={searchCityInputRef}
                            filterBy="cities"
                            placeListener={setCity}
                            onBlur={() => {
                                setShowCitySelect(false);
                            }}
                            autofocus
                        />
                        <button
                            type="button"
                            className="date-input__icon-container"
                            onClick={() => {
                                setShowCitySelect(false);
                            }}
                        >
                            <Icon.Close.Small />
                        </button>
                    </div>
                )}{" "}
            </Flex.Column>
        </HandleClicksOutside>
    );
};
